// ChatApiProvider.js
import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { v4 as uuidv4 } from 'uuid';
import Cookies from 'js-cookie';
import { io } from 'socket.io-client';

import useApiService from './ApiService';
import CONFIG from './CONFIG';

// ✅ Image / blob helpers you already use elsewhere
import {
  ensureCompressedImage,
  compressImageBlob,
  dataURLtoBlob,
  base64ToBlob,
  blobToDataURL,
  IMG_TARGET_MAX_BYTES,
} from './imageCompressor';

// =====================================================================================
// Simple platform flags
// =====================================================================================
const IS_MOBILE = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
const IS_IOS =
  /iP(ad|hone|od)/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

// --- Cookie lifetime ---------------------------------------------------------
// 24 hours (used everywhere we set "ui-<channelId>" pointer cookies)
const COOKIE_TTL_DAYS = 1; // 24h
const COOKIE_TTL_MS = COOKIE_TTL_DAYS * 24 * 60 * 60 * 1000; // handy if needed

const MOBILE_FORCE_DATA_URL = IS_MOBILE; // Prefer data: on mobile so it survives reloads/iframes
const PREFER_DATA_URL = IS_MOBILE; // Conservative default
const PREFER_VAULT_URL = false; // Flip to true only if you have a SW route for vault URLs

// Stub for potential SW-based vault URL (kept false by default so it’s never used accidentally)
const makeVaultUrl = (ref) =>
  `/__sure_vault/${encodeURIComponent(ref?.vault || '')}/${encodeURIComponent(ref?.key || 'file')}`;

// --- CHIPS helpers (partitioned cookies) ------------------------------------
const getCookie = (name) => {
  const needle = `${encodeURIComponent(name)}=`;
  const matches = document.cookie
    .split(';')
    .map((c) => c.trim())
    .filter((c) => c.startsWith(needle));

  if (!matches.length) return null;

  const raw = matches[matches.length - 1].slice(needle.length);
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};

// Are we running inside an iframe?
const IN_IFRAME = (() => {
  try {
    return window.top !== window.self;
  } catch {
    return true;
  }
})();

// WebKit/Safari detection (desktop Safari or any iOS browser)
const isWebKitSafari = () => {
  const ua = navigator.userAgent || '';
  const isWebKit = /AppleWebKit/i.test(ua);
  const isChromiumBrand = /Chrome|Chromium|CriOS|Edg|OPR|OPiOS|EdgiOS/i.test(ua);
  // On iOS all browsers are WebKit, so IS_IOS stays a strong signal
  return IS_IOS || (isWebKit && !isChromiumBrand);
};

const shouldUseSAA = () =>
  IN_IFRAME &&
  isWebKitSafari() &&
  typeof document.hasStorageAccess === 'function' &&
  typeof document.requestStorageAccess === 'function';

// =====================================================================================
// Partitioned cookies management
// =====================================================================================

const setPartitionedCookie = (name, value, { maxAgeDays = COOKIE_TTL_DAYS, path = '/' } = {}) => {
  const maxAge = Math.round(maxAgeDays * 24 * 60 * 60);

  const loc =
    typeof window !== 'undefined' && window.location
      ? window.location
      : { protocol: '', hostname: '' };
  const isLocal = ['localhost', '127.0.0.1'].includes(loc.hostname || '');
  const https = loc.protocol === 'https:';

  // Base attributes
  const parts = [
    `${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
    `Path=${path}`,
    `Max-Age=${maxAge}`,
  ];

  // If we’re on HTTPS, try proper cross-site cookie semantics
  if (https) {
    parts.push('SameSite=None', 'Secure');

    // CHIPS (Chromium) – harmless no-op on Safari
    parts.push('Partitioned');
  } else {
    // Dev fallback for http://localhost – allow cookie so flows work locally
    if (isLocal) {
      // No SameSite=None allowed without Secure → use Lax in dev
      parts.push('SameSite=Lax');
      // DO NOT add Secure on http
    } else {
      console.warn('[Cookies] Not HTTPS and not localhost → cookie may be rejected.');
    }
  }

  try {
    document.cookie = parts.join('; ');
  } catch (e) {
    console.warn('[Cookies] Failed to set cookie:', e);
  }
};

const deletePartitionedCookie = (name, { path = '/' } = {}) => {
  const loc =
    typeof window !== 'undefined' && window.location
      ? window.location
      : { protocol: '', hostname: '' };
  const isLocal = ['localhost', '127.0.0.1'].includes(loc.hostname || '');
  const https = loc.protocol === 'https:';

  const parts = [
    `${encodeURIComponent(name)}=`,
    `Path=${path}`,
    `Expires=Thu, 01 Jan 1970 00:00:00 GMT`,
  ];

  if (https) {
    // match how it was set on HTTPS
    parts.push('SameSite=None', 'Secure', 'Partitioned');
  } else if (isLocal) {
    // match how it was set on http://localhost
    parts.push('SameSite=Lax');
    // DO NOT add Secure on http
  } else {
    // best-effort fallback (warn like in setPartitionedCookie)
    console.warn('[Cookies] Not HTTPS and not localhost → delete may not match cookie attributes.');
  }

  try {
    document.cookie = parts.join('; ');
  } catch (e) {
    console.warn('[Cookies] Failed to delete cookie:', e);
  }
};

// =====================================================================================
// Cookie watcher: reload once if a required cookie disappears
// =====================================================================================
const COOKIE_RELOAD_FLAG = '__sure_cookie_reloaded_once__';

function startCookieWatcher(cookieName, { intervalMs = 5000 } = {}) {
  // per-cookie flag key (prevents cross-channel interference)
  const FLAG = `__sure_cookie_reloaded_once__:${cookieName}`;

  const alreadyReloaded = () => {
    try {
      return sessionStorage.getItem(FLAG) === '1';
    } catch {
      return false;
    }
  };
  const markReloaded = () => {
    try {
      sessionStorage.setItem(FLAG, '1');
    } catch {}
  };
  const clearReloaded = () => {
    try {
      sessionStorage.removeItem(FLAG);
    } catch {}
  };

  let id; // make sure stop closes over the real id

  const check = () => {
    const present = !!getCookie(cookieName);

    if (present) {
      // cookie is healthy again → allow future reloads if it disappears later
      clearReloaded();
      return;
    }

    // cookie missing
    if (!alreadyReloaded()) {
      markReloaded();
      if (typeof window !== 'undefined' && window.location) {
        window.location.reload();
      }
    }
    stop(); // stop watching in this run (prevents tight loops)
  };

  const onVis = () => {
    if (document.visibilityState === 'visible') check();
  };

  const stop = () => {
    clearInterval(id);
    document.removeEventListener('visibilitychange', onVis);
  };

  id = setInterval(check, intervalMs);
  document.addEventListener('visibilitychange', onVis);

  try {
    check();
  } catch {}

  return stop;
}

// =====================================================================================
// END OF Cookie watcher: reload once if a required cookie disappears
// =====================================================================================

// =====================================================================================
// LocalStore facade bound to cookie visibility
// =====================================================================================

const GetStoreItem = (key) => {
  const c = getCookie(key);
  if (c) {
    return localStorage.getItem(key);
  } else {
    RemoveStoreItem(key);
    return null;
  }
};

// const SetStoreItem = (key, value) => {
//   setPartitionedCookie(key, key, { maxAgeDays: COOKIE_TTL_DAYS });
//   localStorage.setItem(key, value);
// };

const SetStoreItem = (key, value) => {
  // ensure any stale cookie with the same name is removed first
  try {
    deletePartitionedCookie(key);
  } catch {}
  setPartitionedCookie(key, key, { maxAgeDays: COOKIE_TTL_DAYS });
  localStorage.setItem(key, value);
};

const GetJsonStoreItem = (key) => {
  const raw = GetStoreItem(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (e) {
    console.error('JSON parse error:', e);
    return null;
  }
};

const SetJsonStoreItem = (key, value) => {
  try {
    SetStoreItem(key, JSON.stringify(value));
  } catch (e) {
    console.error('JSON stringify error:', e);
  }
};

const RemoveStoreItem = (key) => {
  // Kick off vault purge (don’t block caller)
  try {
    purgeVaultsForStoreKey(key);
  } catch {}
  deletePartitionedCookie(key);
  try {
    localStorage.removeItem(key);
  } catch {}
};

// ============================================================================
// Asset Vault (IndexedDB) — one DB ("vault") per asset
// ============================================================================
const VAULT_REG_KEY = '__sure_vault_registry__';

const readVaultRegistry = () => {
  try {
    return JSON.parse(localStorage.getItem(VAULT_REG_KEY) || '[]');
  } catch {
    return [];
  }
};
const writeVaultRegistry = (list = []) => {
  try {
    localStorage.setItem(VAULT_REG_KEY, JSON.stringify([...new Set(list)]));
  } catch {}
};
const registerVault = (name) => writeVaultRegistry([...readVaultRegistry(), name]);
const unregisterVault = (name) => writeVaultRegistry(readVaultRegistry().filter((v) => v !== name));

const listSureVaults = async () => {
  if (typeof indexedDB?.databases === 'function') {
    try {
      const dbs = await indexedDB.databases();
      return (dbs || [])
        .map((d) => d.name)
        .filter(Boolean)
        .filter((n) => n.startsWith('sure_vault_'));
    } catch {
      /* ignore */
    }
  }
  return readVaultRegistry();
};

const openVaultDB = (dbName) =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('bin')) db.createObjectStore('bin');
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const vaultSaveBlob = async (blob, meta = {}) => {
  const dbName = `sure_vault_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  const db = await openVaultDB(dbName);
  await new Promise((resolve, reject) => {
    const tx = db.transaction('bin', 'readwrite');
    tx.objectStore('bin').put({ blob, meta }, 'file');
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
  });
  db.close();
  registerVault(dbName);
  return { vault: dbName, key: 'file' };
};

const vaultGetBlob = async ({ vault, key = 'file' }) => {
  const db = await openVaultDB(vault);
  const data = await new Promise((resolve, reject) => {
    const tx = db.transaction('bin', 'readonly');
    const req = tx.objectStore('bin').get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  db.close();
  if (!data || !data.blob) throw new Error('Asset not found in vault');
  return data.blob;
};

const vaultGetObjectURL = async (ref) => {
  const blob = await vaultGetBlob(ref);
  return URL.createObjectURL(blob);
};

// Quick debug console helpers
window.SureVault = {
  list: async () =>
    (await indexedDB.databases?.())?.filter((db) => db.name && db.name.startsWith('sure_vault_')) ||
    [],
  peekUrl: async (vaultName) => {
    const url = await vaultGetObjectURL({ vault: vaultName, key: 'file' });
    return url;
  },
  delete: async (vaultName) => {
    await new Promise((res, rej) => {
      const req = indexedDB.deleteDatabase(vaultName);
      req.onsuccess = res;
      req.onerror = () => rej(req.error);
      req.onblocked = () => console.warn('Delete blocked:', vaultName);
    });
    unregisterVault(vaultName);
  },
};

// --- Collect vault names that are referenced by messages in LS ---
const collectVaultsFromMessages = (messages = []) => {
  const set = new Set();
  for (const m of messages) {
    if (m?.typeOfMedia === 'image' && Array.isArray(m.images)) {
      for (const im of m.images) if (im?.assetRef?.vault) set.add(im.assetRef.vault);
    }
    if (m?.typeOfMedia === 'file') {
      if (Array.isArray(m.multipleFiles)) {
        for (const f of m.multipleFiles) if (f?.fileRef?.vault) set.add(f.fileRef.vault);
      } else if (m?.fileRef?.vault) {
        set.add(m.fileRef.vault);
      }
    }
    if ((m?.typeOfMedia === 'audio' || m?.typeOfMedia === 'audio_message') && m?.audioRef?.vault) {
      set.add(m.audioRef.vault);
    }
  }
  return Array.from(set);
};

// --- Delete a single vault DB and unregister it ---
const deleteVault = (name) =>
  new Promise((resolve) => {
    const req = indexedDB.deleteDatabase(name);
    req.onsuccess = () => {
      try {
        unregisterVault(name);
      } catch {}
      resolve(true);
    };
    req.onerror = () => {
      console.warn('[Vault] Delete failed for', name, req.error);
      resolve(false);
    };
    req.onblocked = () => {
      console.warn('[Vault] Delete blocked for', name);
      resolve(false);
    };
  });

// --- Purge all vaults referenced by a specific LS key (fire-and-forget safe) ---
const purgeVaultsForStoreKey = async (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return;
    const snap = JSON.parse(raw);
    const messages = Array.isArray(snap?.messages) ? snap.messages : [];
    const vaults = collectVaultsFromMessages(messages);
    if (!vaults.length) return;
    for (const v of vaults) {
      try {
        await deleteVault(v);
      } catch (e) {
        console.warn('[Purge] Failed deleting', v, e);
      }
    }
  } catch (e) {
    console.warn('[Purge] purgeVaultsForStoreKey error for', key, e);
  }
};

// --- Convenience: purge a channel’s LS + its vaults in one call ---
const purgeChannelLocalData = async (channelId) => {
  const key = `ui-${channelId}`;
  await purgeVaultsForStoreKey(key);
  // After vaults are deleted, drop the LS key & cookie
  deletePartitionedCookie(key);
  try {
    localStorage.removeItem(key);
  } catch {}
};

// ============================================================================
// Helper for mobile asset urls / salvage
// ============================================================================
const isHttpUrl = (u = '') => /^https?:\/\//i.test(u);
const isBlobURL = (u = '') => typeof u === 'string' && u.startsWith('blob:');

const salvageImageWithoutRef = async (im) => {
  if (!IS_MOBILE) return { ...im };
  try {
    if (im.base64) {
      const blob = dataURLtoBlob(im.base64);
      const assetRef = await vaultSaveBlob(blob, {
        kind: 'image',
        size: blob.size,
        mime: blob.type || 'image/jpeg',
      });
      return {
        ...im,
        assetRef,
        base64: undefined,
        url: undefined,
        src: undefined,
        size: blob.size,
      };
    }
    if (isHttpUrl(im.url || im.src)) {
      const resp = await fetch(im.url || im.src);
      const blob = await resp.blob();
      const assetRef = await vaultSaveBlob(blob, {
        kind: 'image',
        size: blob.size,
        mime: blob.type,
      });
      return {
        ...im,
        assetRef,
        base64: undefined,
        url: undefined,
        src: undefined,
        size: blob.size,
      };
    }
  } catch (e) {
    console.warn('[Vault] salvageImageWithoutRef failed', e);
  }
  return { ...im, url: undefined, src: undefined, base64: undefined };
};

const salvageFileWithoutRef = async (f) => {
  if (!IS_MOBILE) return { ...f };
  try {
    if (isHttpUrl(f.url)) {
      const resp = await fetch(f.url);
      const blob = await resp.blob();
      const fileRef = await vaultSaveBlob(blob, {
        kind: 'file',
        name: f.fileName,
        ext: f.extension,
        mime: blob.type,
      });
      return { ...f, fileRef, url: undefined };
    }
  } catch (e) {
    console.warn('[Vault] salvageFileWithoutRef failed', e);
  }
  return { ...f, url: undefined };
};

const salvageAudioWithoutRef = async (m) => {
  if (!IS_MOBILE) return { ...m };
  try {
    if (m.audioDataUrl) {
      const blob = base64ToBlob(m.audioDataUrl);
      const audioRef = await vaultSaveBlob(blob, {
        kind: 'audio',
        dur: m.playbackTime,
        mime: blob.type,
      });
      return { ...m, audioRef, audioDataUrl: undefined, audioBlobUrl: undefined, file: undefined };
    }
    if (isHttpUrl(m.file)) {
      const resp = await fetch(m.file);
      const blob = await resp.blob();
      const audioRef = await vaultSaveBlob(blob, {
        kind: 'audio',
        dur: m.playbackTime,
        mime: blob.type,
      });
      return { ...m, audioRef, audioDataUrl: undefined, audioBlobUrl: undefined, file: undefined };
    }
  } catch (e) {
    console.warn('[Vault] salvageAudioWithoutRef failed', e);
  }
  return { ...m, audioDataUrl: undefined, audioBlobUrl: undefined, file: undefined };
};

// ============================================================================
// Offload heavy fields before saving to LS, and rehydrate on load
// ============================================================================
const offloadImage = async (img) => {
  if (img.assetRef) {
    return { stored: { ...img, base64: undefined, url: undefined, src: undefined } };
  }

  if (img.__compressed && img.base64) {
    const blob = dataURLtoBlob(img.base64);
    const assetRef = await vaultSaveBlob(blob, {
      kind: 'image',
      size: blob.size,
      mime: blob.type || 'image/jpeg',
    });
    return {
      stored: {
        ...img,
        assetRef,
        base64: undefined,
        url: undefined,
        src: undefined,
        size: blob.size,
      },
      ref: assetRef,
    };
  }

  let blob = null;
  if (img.base64) blob = dataURLtoBlob(img.base64);
  else if (isBlobURL(img.url)) blob = await (await fetch(img.url)).blob();
  else if (isBlobURL(img.src)) blob = await (await fetch(img.src)).blob();

  if (!blob) return { stored: { ...img, base64: undefined } };

  const blobForStore = blob.size > IMG_TARGET_MAX_BYTES ? await compressImageBlob(blob) : blob;
  const assetRef = await vaultSaveBlob(blobForStore, {
    kind: 'image',
    size: blobForStore.size,
    mime: blobForStore.type || blob.type || 'application/octet-stream',
  });

  return {
    stored: {
      ...img,
      assetRef,
      base64: undefined,
      url: undefined,
      src: undefined,
      size: blobForStore.size,
      __compressed: true,
      __origBytes: blob.size,
    },
    ref: assetRef,
  };
};

const offloadFile = async (fileObj) => {
  if (fileObj.fileRef) return { stored: { ...fileObj, base64File: undefined, url: undefined } };
  let blob = null;
  if (fileObj.base64File) blob = dataURLtoBlob(fileObj.base64File);
  else if (isBlobURL(fileObj.url)) blob = await (await fetch(fileObj.url)).blob();
  if (!blob) return { stored: { ...fileObj, base64File: undefined } };
  const fileRef = await vaultSaveBlob(blob, {
    kind: 'file',
    name: fileObj.fileName,
    ext: fileObj.extension,
    mime: blob.type,
  });
  return { stored: { ...fileObj, fileRef, base64File: undefined, url: undefined }, ref: fileRef };
};

const offloadAudio = async (msg) => {
  if (msg.audioRef) {
    return {
      stored: {
        ...msg,
        audioRef: msg.audioRef,
        audioDataUrl: undefined,
        audioBlobUrl: undefined,
        file: undefined,
      },
    };
  }
  let blob = null;
  if (msg.audioDataUrl) blob = base64ToBlob(msg.audioDataUrl);
  else if (isBlobURL(msg.audioBlobUrl)) blob = await (await fetch(msg.audioBlobUrl)).blob();
  else if (isBlobURL(msg.file)) blob = await (await fetch(msg.file)).blob();
  if (!blob) return { stored: { ...msg, audioDataUrl: undefined } };
  const audioRef = await vaultSaveBlob(blob, {
    kind: 'audio',
    dur: msg.playbackTime,
    mime: blob.type,
  });
  return {
    stored: { ...msg, audioRef, audioDataUrl: undefined, audioBlobUrl: undefined, file: undefined },
    ref: audioRef,
  };
};

const offloadMessageAssets = async (m) => {
  const msg = { ...m };

  if (msg.typeOfMedia === 'image' && Array.isArray(msg.images) && msg.images.length) {
    const results = await Promise.all(msg.images.map(offloadImage));
    msg.images = results.map((r) => r.stored);
  }

  if (msg.typeOfMedia === 'file') {
    if (Array.isArray(msg.multipleFiles) && msg.multipleFiles.length) {
      const outs = await Promise.all(msg.multipleFiles.map(offloadFile));
      msg.multipleFiles = outs.map((o) => o.stored);
      msg.url = undefined;
      msg.base64File = undefined;
    } else if (msg.fileName) {
      const { stored } = await offloadFile({
        fileName: msg.fileName,
        extension: msg.extension,
        size: msg.size,
        url: msg.url,
        base64File: msg.base64File,
      });
      msg.url = undefined;
      msg.base64File = undefined;
      msg.fileRef = stored.fileRef;
    }
  }

  if (msg.typeOfMedia === 'audio' || msg.typeOfMedia === 'audio_message') {
    const { stored } = await offloadAudio(msg);
    Object.assign(msg, stored);
  }

  if (msg.typeOfMedia === 'image' && Array.isArray(msg.images)) {
    msg.images = msg.images.map((im) => ({
      ...im,
      base64: undefined,
      src: undefined,
      url: undefined,
    }));
  }

  return msg;
};

const offloadHeavyAssetsForStore = async (messages = []) => {
  const out = [];
  for (const m of messages) out.push(await offloadMessageAssets(m));
  return out;
};

const rehydrateMessageAssets = async (m) => {
  const msg = { ...m };

  // IMAGES
  if (msg.typeOfMedia === 'image' && Array.isArray(msg.images)) {
    const list = [];
    for (const im of msg.images) {
      const imc = { ...im };
      if (im.assetRef) {
        try {
          const blob = await vaultGetBlob(im.assetRef);
          if (MOBILE_FORCE_DATA_URL) {
            const dataUrl = await blobToDataURL(blob);
            imc.base64 = dataUrl;
            imc.url = dataUrl;
            imc.src = dataUrl;
          } else if (PREFER_VAULT_URL && navigator.serviceWorker?.controller) {
            const url = makeVaultUrl(im.assetRef);
            imc.url = url;
            imc.src = url;
            imc.base64 = undefined;
          } else {
            const blobUrl = URL.createObjectURL(blob);
            imc.url = blobUrl;
            imc.src = blobUrl;
            try {
              imc.base64 = await blobToDataURL(blob);
            } catch {}
          }
        } catch (e) {
          console.warn('[Vault] Missing image asset; skipping', im.assetRef?.vault, e);
          imc.assetRef = undefined;
          imc.base64 = undefined;
          imc.url = undefined;
          imc.src = undefined;
        }
      }
      list.push(imc);
    }
    msg.images = list;
  }

  // FILES
  if (msg.typeOfMedia === 'file') {
    const toUrl = async (ref) => {
      if (MOBILE_FORCE_DATA_URL) {
        const blob = await vaultGetBlob(ref);
        return await blobToDataURL(blob);
      }
      if (PREFER_VAULT_URL && navigator.serviceWorker?.controller) {
        return makeVaultUrl(ref);
      }
      return await vaultGetObjectURL(ref);
    };

    if (Array.isArray(msg.multipleFiles) && msg.multipleFiles.length) {
      const list = [];
      for (const f of msg.multipleFiles) {
        const fc = { ...f };
        if (f.fileRef) {
          try {
            fc.url = await toUrl(f.fileRef);
          } catch (e) {
            console.warn('[Vault] Missing file asset; skipping', f.fileRef?.vault, e);
            fc.fileRef = undefined;
            fc.url = undefined;
          }
        }
        list.push(fc);
      }
      msg.multipleFiles = list;
    } else if (msg.fileRef) {
      try {
        msg.url = await toUrl(msg.fileRef);
      } catch (e) {
        console.warn('[Vault] Missing single file asset; skipping', msg.fileRef?.vault, e);
        msg.fileRef = undefined;
        msg.url = undefined;
      }
    }
  }

  // AUDIO
  if (msg.typeOfMedia === 'audio' || msg.typeOfMedia === 'audio_message') {
    if (IS_MOBILE && !msg.audioRef) Object.assign(msg, await salvageAudioWithoutRef(msg));

    if (msg.audioRef) {
      try {
        if (MOBILE_FORCE_DATA_URL || PREFER_DATA_URL) {
          const blob = await vaultGetBlob(msg.audioRef);
          const dataUrl = await blobToDataURL(blob);
          msg.audioBlobUrl = dataUrl;
          msg.file = dataUrl;
          msg.audioDataUrl = dataUrl;
        } else if (PREFER_VAULT_URL && navigator.serviceWorker?.controller) {
          const url = makeVaultUrl(msg.audioRef);
          msg.audioBlobUrl = url;
          msg.file = url;
          msg.audioDataUrl = undefined;
        } else {
          const blob = await vaultGetBlob(msg.audioRef);
          const blobUrl = URL.createObjectURL(blob);
          msg.audioBlobUrl = blobUrl;
          msg.file = blobUrl;
          try {
            msg.audioDataUrl = await blobToDataURL(blob);
          } catch {}
        }
      } catch (e) {
        console.warn('[Vault] Missing audio asset; skipping', msg.audioRef?.vault, e);
        msg.audioRef = undefined;
        msg.audioBlobUrl = undefined;
        msg.file = undefined;
        msg.audioDataUrl = undefined;
      }
    }
  }

  return msg;
};

const rehydrateMessagesFromStore = async (messages = []) => {
  const out = [];
  for (const m of messages) {
    try {
      out.push(await rehydrateMessageAssets(m));
    } catch (e) {
      console.warn('[Vault] Skipping broken message during rehydrate', m?.id, e);
    }
  }
  return out;
};

const socketAddress = () => {
  return new URL(CONFIG.SERVER).origin;
};

const socketPath = () => {
  if (CONFIG.SERVER.endsWith('/live_chat_api')) {
    return '/live_chat_api' + CONFIG.SOCKET_ROUTE;
  } else {
    return CONFIG.SOCKET_ROUTE;
  }
};

// =============================================================================

// Sort messages stably by timestamp (ascending). Messages without a timestamp
// keep their relative position at the end of the sorted list.
const sortByTimestamp = (msgs = []) =>
  [...msgs].sort((a, b) => {
    const ta = a.timestamp ? new Date(a.timestamp).getTime() : Infinity;
    const tb = b.timestamp ? new Date(b.timestamp).getTime() : Infinity;
    return ta - tb;
  });

// =============================================================================

export const ChatApiContext = createContext(null);

export function ChatApiProvider({ children }) {
  const { channelId } = useParams();
  const purgedForThisChannelRef = useRef(false);
  const api = useApiService();
  const getRef = useRef(api.get);
  const postRef = useRef(api.post);

  useEffect(() => {
    getRef.current = api.get;
    postRef.current = api.post;
  }, [api.get, api.post]);

  // Track the very first assistant message + a safety timeout for typing
  const firstAssistantSeenRef = useRef(false);
  const typingTimeoutRef = useRef(null);

  // Parent-origin handshake storage (persist across messages)
  const parentOriginRef = useRef(null);
  const pendingPostToParentRef = useRef([]);

  const [isActive, setIsActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isOnline, setIsOnline] = useState(false);

  const [isChannelActive, setIsChannelActive] = useState(false);
  const [channelSettings, setChannelSettings] = useState({});
  const [channelMode, setChannelMode] = useState();
  const [channelStyle, setChannelStyle] = useState([]);
  const [faqQuestions, setFaqQuestions] = useState();
  const [adBox, setAdBox] = useState();

  const [userId, setUserId] = useState();
  const [userName, setUserName] = useState();
  const [userPhone, setUserPhone] = useState();
  const [userEmail, setUserEmail] = useState();
  const [threadId, setThreadId] = useState();

  const threadIdRef = useRef();
  const userIdRef = useRef();

  const [isTyping, setIsTyping] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isHydrated, setIsHydrated] = useState(false);

  const [socket, setSocket] = useState(
    io(socketAddress(), {
      transports: [CONFIG.SOCKET_TRANSPORT],
      path: socketPath(),
      autoConnect: false,
      upgrade: false ? CONFIG.SOCKET_TRANSPORT === 'websocket' : undefined,
    }),
  );

  // =====================================================================================
  // Pending button loaders (in-message buttons)
  // =====================================================================================
  // Pending button loaders (in-message buttons)
  const pendingButtonsRef = useRef(new Map());
  const addLoader = (id, btn) => {
    try {
      btn.classList.add('button-loader');
    } catch {}
    pendingButtonsRef.current.set(id, btn);
  };
  const clearLoader = (id) => {
    if (id && pendingButtonsRef.current.has(id)) {
      const btn = pendingButtonsRef.current.get(id);
      try {
        btn.classList.remove('button-loader');
      } catch {}
      pendingButtonsRef.current.delete(id);
      return;
    }
    for (const [, btn] of pendingButtonsRef.current) {
      try {
        btn.classList.remove('button-loader');
      } catch {}
    }
    pendingButtonsRef.current.clear();
  };
  const getOldestPendingId = () => {
    const it = pendingButtonsRef.current?.keys?.();
    const first = it && it.next && it.next();
    return first && !first.done ? first.value : null;
  };
  const resolveLocalPendingBubble = (replyId) => {
    if (!replyId) return;
    setMessages((prev) =>
      prev.map((m) => (m._pendingReplyFor === replyId ? { ...m, _pendingReplyFor: null } : m)),
    );
  };
  const truthy = (v) => /^(1|true|yes|on)$/i.test(String(v || '').trim());

  // =====================================================================================
  // Socket activate
  // =====================================================================================

  // --- Safari Storage Access helpers -----------------------------------------
  const isSafari = () =>
    /^((?!chrome|android).)*safari/i.test(navigator.userAgent) ||
    (!!navigator.userAgentData &&
      navigator.userAgentData.brands?.some((b) => /safari/i.test(b.brand)));

  async function ensureStorageAccess() {
    // Request Storage Access ONLY where it is actually needed —
    // Safari/WebKit inside an iframe. In Chrome/Firefox we rely on
    // partitioned (CHIPS) cookies, so we do NOT call requestStorageAccess()
    // and do not show the user an "allow cookies" prompt.
    if (!shouldUseSAA()) return true;
    try {
      const has = await document.hasStorageAccess();
      if (has) return true;
      // Must be called in a user gesture (our activate() is user-initiated)
      await document.requestStorageAccess();
      return true;
    } catch (e) {
      console.warn('[SAA] requestStorageAccess failed:', e);
      return false;
    }
  }

  // async function ensureStorageAccess() {
  //   // Only attempt SAA where it actually applies (Safari/WebKit iframed)
  //   if (!shouldUseSAA()) return true;

  //   try {
  //     const has = await document.hasStorageAccess();
  //     if (has) return true;

  //     // Must be called in a user gesture; activate() is user-initiated
  //     await document.requestStorageAccess();
  //     return true;
  //   } catch (e) {
  //     // Common Safari case: NotAllowedError if the user hasn't visited your origin top-level
  //     // Don't spam console; just proceed in cookie-less/partitioned mode.
  //     if ((e && e.name === 'NotAllowedError') ||
  //         /not allowed|denied|never been interacted/i.test(String(e && e.message))) {
  //       return false;
  //     }
  //     console.warn('[SAA] unexpected failure:', e);
  //     return false;
  //   }
  // }

  // Listen for parent handshake ONCE
  useEffect(() => {
    const onMsg = (e) => {
      if (e.data?.type !== 'sure_parent_ready') return;

      parentOriginRef.current = e.origin;

      // flush any queued events (if user sent before handshake arrived)
      const queued = pendingPostToParentRef.current.splice(0);
      for (const payload of queued) {
        try {
          window.parent.postMessage(payload, parentOriginRef.current);
        } catch (err) {
          console.warn('[IFRAME] queued postMessage failed:', err);
        }
      }
    };

    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  // Watch the pointer cookie for this channel; reload if it expires or gets deleted
  useEffect(() => {
    if (!channelId) return;
    const key = `ui-${channelId}`; // this is the cookie name you already set
    const stop = startCookieWatcher(key, { intervalMs: 5000 });
    return () => stop && stop();
  }, [channelId]);

  // Optionally refresh our “pointer” cookies after SAA is granted
  function refreshPointerCookieAfterSAA(channelId) {
    const key = `ui-${channelId}`;
    const existing = localStorage.getItem(key);
    if (existing) {
      // re-drop the cookie now that we have access
      setPartitionedCookie(key, key, { maxAgeDays: COOKIE_TTL_DAYS });
    }
  }

  const activate = async () => {
    // 🔑 Ask Safari for storage access on user gesture
    const ok = await ensureStorageAccess();
    if (ok) {
      refreshPointerCookieAfterSAA(channelId);
    }

    setSocket((s) => {
      s.off('connect');
      s.off('disconnect');
      s.off('stream');
      s.off('execute');
      s.on('connect', onConnect);
      s.on('disconnect', onDisconnect);
      s.on('stream', onMessage);
      s.on('execute', onExecute);

      if (s.connected) s.disconnect();
      s.connect();
      return s;
    });
  };

  // --- Initial load: user + thread + messages -------------------------------
  // Initial load (use postRef)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const key = `ui-${channelId}`;
      const userInfo = GetJsonStoreItem(key);

      if (userInfo && userInfo.userId && userInfo.threadId) {
        setUserId(userInfo.userId);
        userIdRef.current = userInfo.userId;
        setUserName(userInfo.userName);
        setUserPhone(userInfo.userPhone);
        setUserEmail(userInfo.userEmail);
        setThreadId(userInfo.threadId);
        threadIdRef.current = userInfo.threadId;

        try {
          const response = await postRef.current(
            `/channels/${channelId}/threads/${userInfo.threadId}`,
          );
          if (!cancelled && response?.status === true) {
            const rehydrated = await rehydrateMessagesFromStore(userInfo.messages ?? []);
            if (!cancelled) setMessages(sortByTimestamp(rehydrated));
          }
        } catch (e) {
          console.error('Error fetching thread status:', e);
        } finally {
          if (!cancelled) setIsHydrated(true);
        }
      } else {
        const user = `u-${uuidv4()}`;
        const thread = `t-${uuidv4()}`;
        setUserId(user);
        userIdRef.current = user;
        setThreadId(thread);
        threadIdRef.current = thread;

        // ⚡ Write synchronously to prevent race condition with sendMessage/snapshotUserInfo
        const initPayload = {
          userId: user,
          threadId: thread,
          userName: '',
          userPhone: '',
          userEmail: '',
          messages: [],
        };
        SetJsonStoreItem(key, initPayload);

        setIsHydrated(true);

        // NEW CHAT: show typing until first assistant message (or timeout)
        firstAssistantSeenRef.current = false;
        setIsTyping(true);
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 15000);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [channelId]);

  // =====================================================================================
  // END OF Socket activate
  // =====================================================================================

  // =====================================================================================
  // Channel settings
  // =====================================================================================

  useEffect(() => {
    let cancelled = false;
    // new channel → allow a new purge decision
    purgedForThisChannelRef.current = false;

    (async () => {
      setLoading(true);
      try {
        const response = await getRef.current(`/channels/${channelId}`);
        if (!cancelled && response) {
          setIsChannelActive(response.isActive);
          setChannelSettings(response.settings);
          setChannelMode(response.mode);
          setChannelStyle(response.styles);
          setAdBox(response.adBoxData);
          setFaqQuestions(response.faqQuestions);

          // 🔥 If backend says channel is NOT active → purge local LS + vaults now
          if (!response.isActive && !purgedForThisChannelRef.current) {
            try {
              await purgeChannelLocalData(channelId);
            } catch {}
            purgedForThisChannelRef.current = true;
          }
        }
      } catch (e) {
        setIsChannelActive(false);
        console.error('Error fetching channel settings:', e);

        // 🔥 On error we also treat as inactive → purge once
        if (!purgedForThisChannelRef.current) {
          try {
            await purgeChannelLocalData(channelId);
          } catch {}
          purgedForThisChannelRef.current = true;
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [channelId]); // ✅ only channelId

  useEffect(() => {
    setIsActive(loading === false && isChannelActive === true);
  }, [loading, isChannelActive]);

  useEffect(() => {
    if (!socket) return;
    return () => {
      try {
        socket.off('connect');
        socket.off('disconnect');
        socket.off('stream');
        socket.off('execute');
        socket.disconnect();
      } catch {}
      clearTimeout(typingTimeoutRef.current);
    };
  }, [socket]);

  // Rebuild the store messages by reusing already-offloaded items (matched by id)
  // and only offloading messages we haven't stored yet.
  const rebuildStoreMessages = async (prevStored = [], liveMsgs = []) => {
    const prevById = new Map((prevStored || []).map((m) => [m.id, m]));
    const out = [];

    for (const m of liveMsgs || []) {
      const cached = prevById.get(m.id);

      if (cached) {
        const merged = { ...cached };

        if (Array.isArray(m.reactions)) {
          merged.reactions = m.reactions;
        }

        out.push(merged);
      } else {
        out.push(await offloadMessageAssets(m)); // offload only new message
      }
    }

    return out;
  };

  const snapshotUserInfo = ({ user, thread, name, phone, email, msgs }) => {
    setTimeout(async () => {
      const key = `ui-${channelId}`;
      const prev = GetJsonStoreItem(key) ?? {};
      const payload = {
        userId: user ?? prev.userId,
        userName: name ?? prev.userName,
        userPhone: phone ?? prev.userPhone,
        userEmail: email ?? prev.userEmail,
        threadId: thread ?? prev.threadId,
        messages: prev.messages || [],
      };

      // 🛡️ Never write a snapshot without userId/threadId — data would be corrupted
      if (!payload.userId || !payload.threadId) {
        console.warn('[snapshotUserInfo] Skipping write — missing userId or threadId');
        return;
      }

      if (Array.isArray(msgs)) {
        try {
          payload.messages = await rebuildStoreMessages(prev.messages || [], msgs);
        } catch (e) {
          console.warn('[Vault] offload failed, storing raw messages as fallback:', e);
          payload.messages = msgs;
        }
      }

      SetJsonStoreItem(key, payload);
    }, 10);
  };

  const createUserName = (value) => {
    if (!value) return;
    setUserName(value);
    snapshotUserInfo({ name: value });
  };
  const createUserEmail = (value) => {
    if (!value) return;
    setUserEmail(value);
    snapshotUserInfo({ email: value });
  };
  const createUserPhone = (value) => {
    if (!value) return;
    setUserPhone(value);
    snapshotUserInfo({ phone: value });
  };

  // --- Socket handlers -------------------------------------------------------
  const onConnect = () => {
    setIsOnline(true);
    const timeoutId = setTimeout(() => {
      socket.emit('handshake', {
        channel_id: channelId,
        thread_id: threadIdRef.current,
        user_id: userIdRef.current,
        user_name: userName ?? '',
        user_phone: userPhone ?? '',
        user_email: userEmail ?? '',
      });
      clearTimeout(timeoutId);
    }, 100);
  };
  const onDisconnect = () => setIsOnline(false);

  const onExecute = (message, ack) => {
    onMessage(message);
    if (ack && typeof ack === 'function') {
      ack({ status: true });
    }
  };

  const onMessage = (message) => {
    if (message?.type === 'message_reactions') {
      applyMessageReactionsEvent(message);
      return;
    }
    if (message.channel === 'technicalMessages') {
      if (message.type === 'typing') {
        if (message.typing === 'start') setIsTyping(true);
        else if (message.typing === 'stop') setIsTyping(false);
        return;
      }

      if (message.type === 'message_updated' || message.type === 'message_reaction_result') {
        patchMessageInThread(message.payload || message.message || message);
        return;
      }

      return;
    }

    // If this is the first assistant message in a brand-new chat, stop the loader
    if (message.sender === 'assistant' && !firstAssistantSeenRef.current) {
      firstAssistantSeenRef.current = true;
      clearTimeout(typingTimeoutRef.current);
      setIsTyping(false);
    }

    // --- CLEAR BUTTON LOADER(s) via correlation -----------------------------
    const replyToId =
      message?.in_reply_to?.client_action_id ||
      message?.client_action_id ||
      message?.meta?.client_action_id ||
      message?.action_id ||
      null;

    if (replyToId) {
      clearLoader(replyToId);
      resolveLocalPendingBubble(replyToId);
    } else if (message.sender === 'assistant' && pendingButtonsRef.current.size > 0) {
      const oldest = getOldestPendingId();
      if (oldest) {
        clearLoader(oldest);
        resolveLocalPendingBubble(oldest);
      }
    }
    // ------------------------------------------------------------------------

    setMessages((prev) => {
      if (message.sender === 'user') {
        if (message.parent_client_id) {
          const parentExists = prev.some((m) => m.id === message.parent_client_id);
          if (parentExists) {
            return prev;
          }
        }
        if (/-img\d+$/.test(message.id)) {
          const baseId = message.id.replace(/-img\d+$/, '');
          const baseExists = prev.some((m) => m.id === baseId);
          if (baseExists) {
            return prev;
          }
        }
      }

      const idx = prev.findIndex((m) => m.id === message.id);

      let next;
      if (idx === -1) {
        next = sortByTimestamp([
          ...prev,
          {
            id: message.id,
            text: message.text,
            timestamp: message.timestamp,
            sender: message.sender,
            type: message.type,
            chatId: message.chatId,
            audio: message.audio,
            files: message.files,
            images: message.images,
            reactions: message.reactions,
            ...message,
          },
        ]);
      } else {
        // Update in place — no need to re-sort since position stays the same
        next = prev.map((m) => (m.id === message.id ? { ...m, ...message } : m));
      }

      snapshotUserInfo({ msgs: next });
      setIsTyping(false);
      return next;
    });
  };

  // ===========================================================================
  // Image fan-out: emit images as separate chat messages (single local bubble)
  // ===========================================================================
  const buildLeanImagePart = (base, img, idx, total) => {
    const isFirst = idx === 0;
    const originalText = typeof base.text === 'string' ? base.text : '';

    return {
      // Unique id for this part
      id: `${base.id}-img${idx + 1}`,
      // Identity
      sender: base.sender ?? 'user',
      user: base.user ?? userId,
      user_name: base.user_name ?? userName ?? '',
      user_phone: base.user_phone ?? userPhone ?? '',
      user_email: base.user_email ?? userEmail ?? '',
      // Routing
      chatId: base.chatId ?? threadId,
      sure_channel: base.sure_channel ?? channelId,
      // Timestamps / type
      timestamp: base.timestamp || new Date().toISOString(),
      typeOfMedia: 'image',
      type: 'image',
      // Payload
      gallery: 'false',
      images: [img],
      // Only the first part carries the original text to avoid duplicates on the server
      text: isFirst ? originalText : '',

      // Correlation metadata (optional but helpful server-side)
      parent_client_id: base.id, // original combined-message id
      part_index: idx + 1, // 1-based
      part_total: total,
    };
  };

  // ===========================================================================
  // END OF Image fan-out: emit images as separate chat messages (single local bubble)
  // ===========================================================================

  // ===========================================================================
  // Socket emit helper. Uses `socket` from component scope.
  // ===========================================================================
  const emitSocketMessage = (msg, meta = {}) => {
    if (socket?.connected) {
      socket.emit('stream', msg);
    } else {
      console.warn('Socket.IO is not connected.');
    }
  };

  // ===========================================================================
  // END OF Socket emit helper. Uses `socket` from component scope.
  // ===========================================================================

  // ===========================================================================
  // sendMessage function
  // ===========================================================================

  const sendMessage = async (message, options = {}) => {
    const silent = options.silent || message.__silent === true;

    // Notify parent page (for GTM)
    const payload = { type: 'sure_message_posted', payload: message };
    const origin = parentOriginRef.current;

    if (origin) {
      try {
        window.parent.postMessage(payload, origin);
      } catch (e) {
        console.warn('[IFRAME] postMessage failed:', e);
      }
    } else {
      // handshake not arrived yet — queue and send once it arrives
      pendingPostToParentRef.current.push(payload);
      console.warn('[IFRAME] parent origin not set yet; queued');
    }

    // ---- LOCAL "SENDING" BUBBLE FOR IMAGE MESSAGES (shows while we compress) ----
    // We append this *before* compression so the user sees immediate feedback.
    let tempId = null;
    if (!silent && message?.typeOfMedia === 'image') {
      tempId = `sending-${message.id}`;
      const placeholder = {
        id: tempId,
        sender: 'user',
        typeOfMedia: 'sending',
        text: '', // no text, just the loader bubble
        timestamp: new Date().toISOString(),
        chatId: threadId,
        sure_channel: channelId,
        user: userId,
      };
      setMessages((prev) => {
        const result = [...prev, placeholder];
        snapshotUserInfo({ msgs: result });
        return result;
      });
    }

    // Compress images before local render / socket emit
    if (message.typeOfMedia === 'image' && Array.isArray(message.images) && message.images.length) {
      const compressedList = [];

      for (let i = 0; i < message.images.length; i++) {
        const original = message.images[i];
        const compressed = await ensureCompressedImage(original);
        compressedList.push(compressed);
      }

      message.images = compressedList;
    }

    // Normalize mandatory fields
    message.chatId = threadId;
    message.user = message.user ?? userId;
    message.user_name = message.user_name ?? userName ?? '';
    message.user_phone = message.user_phone ?? userPhone ?? '';
    message.user_email = message.user_email ?? userEmail ?? '';
    message.sure_channel = channelId;

    // Prevent duplicates only for visible messages
    if (!silent && messages.some((m) => m.id === message.id)) {
      console.error('Duplicate message ID detected:', message.id);
      // remove placeholder if we created one
      if (tempId) {
        setMessages((prev) => prev.filter((m) => m.id !== tempId));
      }
      return;
    }

    // IMAGE FLOW:
    // split into separate messages both locally and for socket emit
    if (message.typeOfMedia === 'image') {
      const imgs = Array.isArray(message.images) ? message.images : [];
      const total = imgs.length || 0;

      const parts =
        total > 0
          ? imgs.map((img, idx) => buildLeanImagePart(message, img, idx, total))
          : [message];

      if (!silent) {
        setMessages((prev) => {
          let base = prev;
          if (tempId) base = prev.filter((m) => m.id !== tempId);

          const result = sortByTimestamp([...base, ...parts]);
          snapshotUserInfo({ msgs: result });
          return result;
        });
      } else {
      }

      parts.forEach((part, idx) => {
        emitSocketMessage(part, {
          kind: 'image',
          idx: idx + 1,
          total: parts.length,
        });
      });

      return;
    }

    // Local append only if NOT silent
    if (!silent) {
      // If we added a placeholder, replace it with the real message
      setMessages((prev) => {
        let base = prev;
        if (tempId) base = prev.filter((m) => m.id !== tempId);
        const result = sortByTimestamp([...base, message]);
        snapshotUserInfo({ msgs: result });
        return result;
      });
    } else {
    }

    // Prepare any special payloads (audio/file) BEFORE emitting
    if (
      message.typeOfMedia === 'audio_message' &&
      typeof message.file === 'string' &&
      message.file.startsWith('blob:')
    ) {
      try {
        const response = await fetch(message.file);
        const blob = await response.blob();
        const fileName = `recorded_audio_${Date.now()}.mp4`;
        const audioFile = new File([blob], fileName, { type: 'audio/mp4' });
        await audioFile.arrayBuffer(); // ensure readable
        message.file = fileName; // keep name if server expects that
      } catch (error) {
        console.error('Error processing audio file:', error);
        return;
      }
    } else if (message.typeOfMedia === 'file') {
      const multiFiles = message.multipleFiles || [];
      for (let f of multiFiles) {
        if (!f.url || !f.url.startsWith('blob:')) {
          continue;
        }
        try {
          const response = await fetch(f.url);
          const blob = await response.blob();
          const theFile = new File([blob], f.fileName, {
            type: f.extension ? `application/${f.extension}` : blob.type,
          });
          await theFile.arrayBuffer();
        } catch (err) {
          console.error('Error uploading file:', f.fileName, err);
        }
      }
    }

    // Emit once for non-image messages
    let kind = message.typeOfMedia || 'unknown';
    if (!message.typeOfMedia || message.typeOfMedia === 'simple_text') kind = 'text';
    if (message.typeOfMedia === 'audio_message') kind = 'audio';

    emitSocketMessage(message, { kind });
  };
  // =============================================================================
  // In-message <button> clicks → optional local render + silent server send + loader
  // =============================================================================
  useEffect(() => {
    const SELECTOR = 'button[data-sure-post="in-message-button-click"]';

    const onClick = (e) => {
      const btn = e.target?.closest?.(SELECTOR);
      if (!btn) return;

      const clientActionId = `act-inmsg-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

      const sendText =
        btn.getAttribute('data-sure-send-text') ||
        btn.getAttribute('data-sure-text') ||
        'Клік по кнопці';

      const shouldRender = truthy(btn.getAttribute('data-sure-render'));
      const renderText = btn.getAttribute('data-sure-render-text') || sendText;

      addLoader(clientActionId, btn);

      const sendPayload = {
        id: clientActionId,
        sender: 'user',
        typeOfMedia: 'simple_text',
        text: sendText,
        action: 'in-message-button-click',
        client_action_id: clientActionId,
        __silent: true,
        // identity & routing (sendMessage will also normalize, this is just explicit)
        user: userId,
        user_name: userName || '',
        user_phone: userPhone || '',
        user_email: userEmail || '',
        chatId: threadId,
        sure_channel: channelId,
        reactions: [],
      };

      try {
        sendMessage(sendPayload, { silent: true });
      } catch (err) {
        console.error('[UI] Failed to post silent message from button:', err);
        clearLoader(clientActionId);
        return;
      }

      if (shouldRender) {
        const localMsg = {
          id: `${clientActionId}-local`,
          sender: 'user',
          typeOfMedia: 'simple_text',
          text: renderText,
          timestamp: new Date().toISOString(),
          chatId: threadId,
          sure_channel: channelId,
          user: userId,
          user_name: userName || '',
          user_phone: userPhone || '',
          user_email: userEmail || '',
          reactions: [],
          _fromInMessageButton: true,
          _pendingReplyFor: clientActionId,
        };
        setMessages((prev) => {
          const result = [...prev, localMsg];
          snapshotUserInfo({ msgs: result });
          return result;
        });
      }

      setTimeout(() => clearLoader(clientActionId), 30_000);
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [sendMessage, threadId, channelId, userId]);

  const patchMessageInThread = (updatedMessage) => {
    if (!updatedMessage || !updatedMessage.id) return;

    setMessages((prev) => {
      let idx = prev.findIndex((m) => m.id === updatedMessage.id);
      let patchData = updatedMessage;

      if (idx === -1 && /-img\d+$/.test(updatedMessage.id)) {
        const baseId = updatedMessage.id.replace(/-img\d+$/, '');
        idx = prev.findIndex((m) => m.id === baseId);
        // беремо тільки реакції, не перезаписуємо інші поля
        patchData = { reactions: updatedMessage.reactions };
      }

      let next;
      if (idx === -1) {
        next = [...prev, updatedMessage];
      } else {
        next = prev.map((m, i) => (i === idx ? { ...m, ...patchData } : m));
      }

      snapshotUserInfo({ msgs: next });
      return next;
    });
  };

  const setMessageReaction = async (messageId, reaction) => {
    const msg = {
      channel: 'technicalMessages',
      type: 'message_reaction',
      operation: 'add',
      user: userId,
      message_id: messageId,
      reaction,
      chatId: threadId,
      sure_channel: channelId,
      __silent: true,
    };

    emitSocketMessage(msg, { kind: 'reaction' });
    return msg;
  };

  const deleteMessageReaction = async (messageId, reaction) => {
    const msg = {
      channel: 'technicalMessages',
      type: 'message_reaction',
      operation: 'delete',
      user: userId,
      message_id: messageId,
      reaction,
      chatId: threadId,
      sure_channel: channelId,
      __silent: true,
    };

    emitSocketMessage(msg, { kind: 'reaction' });
    return msg;
  };

  const applyMessageReactionsEvent = (evt) => {
    const payload = evt?.payload || evt?.message || evt;

    const messageId = payload?.message_id;
    const reactions = payload?.reactions;

    if (!messageId) return;

    patchMessageInThread({
      id: messageId,
      reactions: Array.isArray(reactions) ? reactions : [],
    });
  };

  // =============================================================================

  return (
    <ChatApiContext.Provider
      value={{
        loading,
        isOnline,
        isActive,
        channelSettings,
        adBox,
        faqQuestions,
        isTyping,
        userName,
        userPhone,
        userEmail,
        messages,
        isHydrated,
        createUserName,
        createUserEmail,
        createUserPhone,
        sendMessage,
        activate,
        setMessageReaction,
        deleteMessageReaction,
        userId,
      }}
    >
      {children}
    </ChatApiContext.Provider>
  );
}

export function useChatApi() {
  const context = useContext(ChatApiContext);
  if (!context) throw new Error('useChatApi must be used within a ChatApiContext');
  return context;
}
